import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router-dom";
import Grid from "react-bootstrap/lib/Grid";
import Row from "react-bootstrap/lib/Row";
import Col from "react-bootstrap/lib/Col";
import { func, shape, string, objectOf, bool, oneOfType, arrayOf } from "prop-types";
import classNames from "classnames";

import { actions as layoutActions } from "reducers/layout";
import { actions, selectors } from "reducers/settings";
import { resizableRoute } from "pages/_components/Resizable";
import { dispatchV3ShellLayout, isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import * as i18n from "util/i18n";
import { getArray, get } from "util/config";
import { lowerCaseFirstLetter } from "util/string";
import { Icon, Tooltip } from "@ui-kit/components/index.es";

import Head from "pages/_components/Head";
import I18n from "pages/_components/I18n";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import SwitchField from "pages/_components/fields/SwitchField";
import Image from "pages/_components/Image";
import DrawerModal from "pages/_components/drawer/DrawerModal";
import NotificationsConfigruationsOptionsHint from "./NotificationsConfigruationsOptionsHint";

class NotificationsConfigurationTransports extends Component {
    static propTypes = {
        dispatch: func.isRequired,
        match: shape({
            params: shape({
                communicationType: string.isRequired,
            }),
        }).isRequired,
        communicationTransportsConfigurations: objectOf(oneOfType([bool, string])),
        fetching: bool.isRequired,
        keys: arrayOf(string).isRequired,
        isDesktop: bool.isRequired,
        enabledChannels: arrayOf(string).isRequired,
        embeddedInV3Shell: bool,
    };

    static defaultProps = {
        communicationTransportsConfigurations: null,
        embeddedInV3Shell: false,
    };

    state = {
        showHintDrawer: false,
    };

    componentDidMount() {
        const { dispatch, match, embeddedInV3Shell } = this.props;

        dispatch(actions.communicationTypePre(match.params.communicationType));
        if (embeddedInV3Shell && isUxDashboardImprovementsEnabled()) {
            dispatchV3ShellLayout(dispatch, {
                titleKey: "settings.notificationsConfiguration",
                goBackPath: "/settings/notificationsConfiguration",
                rightContentMobile: null,
                scopes: ["settings"],
            });
        }
    }

    componentWillUnmount() {
        const { dispatch, embeddedInV3Shell } = this.props;
        if (embeddedInV3Shell && isUxDashboardImprovementsEnabled()) {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        }
    }

    areOtherChannelsDisabled = (id) => {
        const { communicationTransportsConfigurations: options } = this.props;
        const otherOptions = Object.keys(options).filter(
            (o) => o.includes("subscribed") && !o.toLowerCase().includes(id),
        );
        return otherOptions.every((option) => options[option] === false);
    };

    toggleSwitch = ({ target }, id, transportConfiguration) => {
        const {
            dispatch,
            match,
            communicationTransportsConfigurations: { options },
        } = this.props;

        if (target.value === false && this.areOtherChannelsDisabled(id)) {
            let newOptions = {};
            Object.keys(options).forEach((option) => {
                newOptions = { ...newOptions, [option]: false };
            });
            dispatch(
                actions.modifyNotificationConfigurationOptions(match.params.communicationType, false, null, newOptions),
            );
        }
        dispatch(
            actions.modifyNotificationConfigurations(
                match.params.communicationType,
                id,
                target.value,
                transportConfiguration,
            ),
        );
    };

    renderDefaultTransports = (defaultTransports) =>
        defaultTransports.length > 0 && (
            <p>
                <I18n id="communications.communicationsTypes.defaultTransports" />
                {Object.keys(defaultTransports).map((defaultTransportIndex) => {
                    const key = defaultTransports[defaultTransportIndex];
                    const id = `userInfo.preferences.notificationsConfiguration.${key.toLowerCase()}.label`;

                    return (
                        <strong key={key}>
                            <I18n id={id} />{" "}
                        </strong>
                    );
                })}
            </p>
        );

    renderCommunicationTransportsConfigurations = (defaultTransports) => {
        const { communicationTransportsConfigurations, keys, enabledChannels, match, isDesktop } = this.props;
        const labelTooltip = `frontend.notificacionesMobile.tooltip.${match.params.communicationType}`;

        return keys.map((transportConfiguration) => {
            if (transportConfiguration.includes("subscribed")) {
                const transportConfigName = transportConfiguration.substring("subscribed".length).toLowerCase();
                const labelChannel = `userInfo.preferences.notificationsConfiguration.${transportConfigName}.label`;
                if (
                    !defaultTransports.includes(transportConfigName.toUpperCase()) &&
                    enabledChannels?.includes(transportConfigName.toUpperCase())
                ) {
                    return (
                        <>
                            <SwitchField
                                name={transportConfigName}
                                key={transportConfigName}
                                label={i18n.get(labelChannel)}
                                value={communicationTransportsConfigurations[transportConfiguration]}
                                onChange={(event) =>
                                    this.toggleSwitch(event, transportConfigName, transportConfiguration)
                                }
                                buttonInfo={
                                    transportConfigName.toUpperCase() === "PUSH" &&
                                    match.params.communicationType === "echeqDepositCustody" && (
                                        <Tooltip
                                            ml={8}
                                            placement="right"
                                            label={get(labelTooltip, "")}
                                            maxW={isDesktop ? "21rem" : "14rem"}>
                                            <Icon name="Info" variant="outline" color="primary-color" size={16} />
                                        </Tooltip>
                                    )
                                }
                            />
                        </>
                    );
                }
            }

            return null;
        });
    };

    toggleOptionsSwitch = ({ target }, option) => {
        const { dispatch, match } = this.props;
        dispatch(actions.modifyNotificationConfigurationOptions(match.params.communicationType, target.value, option));
    };

    renderCommunicationOptions = (options, subscribedAnyChannel, communicationTypeKey) =>
        Object.entries(options).map(([option, enabled]) => {
            const labelSwitchPre = "userInfo.preferences.notificationsConfiguration";
            const defaultLabel = i18n.get(`${labelSwitchPre}.${option.toLowerCase()}.label`);
            return (
                <SwitchField
                    name={option}
                    key={option}
                    label={i18n.get(
                        `${labelSwitchPre}.${communicationTypeKey}.${option.toLowerCase()}.label`,
                        defaultLabel,
                    )}
                    value={enabled}
                    isDisabled={!subscribedAnyChannel}
                    onChange={(event) => this.toggleOptionsSwitch(event, option)}
                />
            );
        });

    renderTitleMobile = () => (
        <div className="title-account-header-multiline-background-blue">
            <h1 style={{ margin: "0.2rem" }}>
                <I18n id="settings.notificationsConfiguration" />
            </h1>
        </div>
    );

    closeDrawer = () => {
        this.setState({ showHintDrawer: false });
    };

    showHintDrawer = () => {
        this.setState({
            showHintDrawer: true,
        });
    };

    renderContent() {
        const { communicationTransportsConfigurations, match, isDesktop } = this.props;
        const { showHintDrawer } = this.state;

        if (communicationTransportsConfigurations) {
            const typesWithoutOptions = ["pending"];
            const communicationTypeKey = match.params?.communicationType;
            const defaultTransports = getArray(
                `core.communications.communicationTypes.${match.params.communicationType}.default`,
            );
            const labelOptions = `settings.notificationsConfiguration.options.${communicationTypeKey}`;

            const defaultTransportsUpper = defaultTransports.map((item) => item.toUpperCase());
            const communicationType = i18n.get(`communications.communicationsTypes.${match.params.communicationType}`);
            const { options } = communicationTransportsConfigurations;
            const subscribedAnyChannel =
                communicationTransportsConfigurations.subscribedDefault ||
                communicationTransportsConfigurations.subscribedFacebook ||
                communicationTransportsConfigurations.subscribedMail ||
                communicationTransportsConfigurations.subscribedPush ||
                communicationTransportsConfigurations.subscribedSMS ||
                communicationTransportsConfigurations.subscribedTwitter;
            if (!subscribedAnyChannel) {
                Object.keys(options).forEach((key) => {
                    options[key] = false;
                });
            }
            const lowerCaseCommunicationType =
                match.params.communicationType !== "echeqDepositCustody"
                    ? lowerCaseFirstLetter(communicationType)
                    : communicationType;
            return (
                <form className="above-the-fold">
                    {showHintDrawer && (
                        <DrawerModal
                            content={
                                <NotificationsConfigruationsOptionsHint
                                    isDesktop={isDesktop}
                                    options={options}
                                    communicationType={communicationType}
                                />
                            }
                            showDrawerRemote={showHintDrawer}
                            isDesktop={isDesktop}
                            width={isDesktop ? "30%" : "100%"}
                            closeRemotely={() => this.closeDrawer()}
                            noBlueHeader
                        />
                    )}
                    <section className="container--layout flex-grow align-items-center pb-5">
                        <Grid className="form-content">
                            <Row className="justify-content-center">
                                <Col sm={12} md={8} lg={6} className="col col-12">
                                    <p className="text-lead text-left">
                                        <I18n id="settings.notificationsConfiguration.description.base" />{" "}
                                        <strong>{lowerCaseCommunicationType}</strong>
                                    </p>
                                    <div className="form-group">
                                        <div className="form-group-control-list">
                                            {this.renderCommunicationTransportsConfigurations(defaultTransportsUpper)}
                                        </div>
                                    </div>
                                    {this.renderDefaultTransports(defaultTransportsUpper)}
                                    {Object.keys(options).length &&
                                    !typesWithoutOptions.includes(communicationTypeKey) ? (
                                        <>
                                            <div className="d-flex">
                                                <p className="text-lead text-left my-25 mr-2">
                                                    <I18n
                                                        id={labelOptions}
                                                        defaultValue={i18n.get(
                                                            "settings.notificationsConfiguration.options",
                                                        )}
                                                    />{" "}
                                                    <strong>{lowerCaseCommunicationType}</strong>
                                                </p>
                                                <div className={classNames("p-relative d-flex")}>
                                                    <button
                                                        type="button"
                                                        className={classNames("btn btn-hint my-0", {
                                                            "p-absolute": isDesktop,
                                                        })}
                                                        onClick={this.showHintDrawer}>
                                                        <Image src="images/infoUserInvite.svg" />
                                                        {isDesktop ? (
                                                            <I18n
                                                                id="echeqs.emit.emision.modes.hint"
                                                                component="p"
                                                                componentProps={{
                                                                    className: "hint-text text-nowrap my-0",
                                                                }}
                                                            />
                                                        ) : (
                                                            undefined
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                            <div
                                                className={classNames("form-group", {
                                                    "pb-9": !isDesktop,
                                                })}>
                                                <div className="form-group-control-list">
                                                    {this.renderCommunicationOptions(
                                                        options,
                                                        subscribedAnyChannel,
                                                        communicationTypeKey,
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        undefined
                                    )}
                                </Col>
                            </Row>
                        </Grid>
                    </section>
                </form>
            );
        }

        return null;
    }

    render() {
        const { fetching, isDesktop, embeddedInV3Shell } = this.props;
        const isShellActive = embeddedInV3Shell && isUxDashboardImprovementsEnabled();

        return (
            <Fragment>
                {!isShellActive && <Notification scopeToShow="settings.notificationsConfiguration.modify" />}
                <div className="admin-detail-head px-0">
                    {(isDesktop || !isShellActive) && (
                        <Head
                            backLinkTo="/settings/notificationsConfiguration"
                            headerClassName={!isDesktop ? "blue-main-header-mobile blue-main-title-mobile" : ""}
                            centerElement={!isDesktop && this.renderTitleMobile}
                            hideMobileMenu={!isDesktop}
                        />
                    )}
                    {isDesktop && (
                        <h1>
                            <I18n id="settings.notificationsConfiguration" />
                        </h1>
                    )}
                </div>
                <MainContainer showLoader={fetching}>{this.renderContent()}</MainContainer>
            </Fragment>
        );
    }
}

const mapStateToProps = (state) => ({
    communicationTransportsConfigurations: selectors.getCommunicationTransportsConfigurations(state),
    fetching: selectors.isFetching(state),
    keys: selectors.getCommunicationTransportsConfigurationsKeys(state),
    enabledChannels: selectors.getEnabledChannels(state),
});

export default withRouter(compose(connect(mapStateToProps), resizableRoute)(NotificationsConfigurationTransports));
